import { fork, all, call, put, select, takeEvery } from 'redux-saga/effects'

// action
import { orderActions } from '@redux/store/main/order.store'

// api
import { orderApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

import {
	OrderCreationType,
	OrderDeletionType,
	OrderUpdateType,
	OrderType,
	Order,
} from '@schemas'

const apiGetOrder = async (query: {
	startDate: string
	endDate: string
	status: string
	searchKeyword: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await orderApi.getOrder({ query: query })
	return res
}

// API call to delete order
async function deleteOrder(orderId: string) {
	return await orderApi.deleteOrder({ params: { orderId } })
}

// API call to update order
async function updateOrder({
	orderId,
	order,
}: {
	orderId: string
	order: OrderUpdateType
}) {
	return await orderApi.updateOrder({ params: { orderId }, body: order })
}

function* registerOrderSaga(action: {
	type: string
	payload: OrderCreationType
}): Generator<unknown, void, unknown> {
	try {
		yield call(orderApi.addOrder, {
			body: action.payload,
		})

		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		yield put(orderActions.REGISTER_ORDER_SUCCESS())
	} catch (error: any) {
		yield put(orderActions.REGISTER_ORDER_FAILURE(error.message))
	}
}

function* deleteOrderSaga(action: {
	type: string
	payload: OrderDeletionType
}): Generator<unknown, void, unknown> {
	try {
		yield call(deleteOrder, action.payload.orderId)
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		yield put(orderActions.DELETE_ORDER_SUCCESS())
	} catch (error: any) {
		yield put(orderActions.DELETE_ORDER_FAILURE(error.message))
	}
}

function* updateOrderSaga(action: {
	type: string
	payload: {
		orderId: string
		order: OrderUpdateType
	}
}): Generator<unknown, void, unknown> {
	try {
		yield call(updateOrder, {
			orderId: action.payload.orderId,
			order: action.payload.order,
		})
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		yield put(orderActions.UPDATE_ORDER_SUCCESS())
	} catch (error: any) {
		yield put(orderActions.UPDATE_ORDER_FAILURE(error.message))
	}
}

function* fetchOrderOptions(): Generator<unknown, void, unknown> {
	yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
}

// 페이지 초기화 관련
function* triggerInitOrderPage(): Generator<unknown, void, unknown> {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error: any) {
		Logger.warn(error)
	}
}

function* triggerOrderSearchSaga() {
	try {
		const searchStartDate: string = yield select(state => state.order.searchStartDate)
		const searchEndDate: string = yield select(state => state.order.searchEndDate)
		const searchStatus: string = yield select(state => state.order.searchStatus)
		const searchKeyword: string = yield select(state => state.order.searchKeyword)
		yield put(orderActions.ORDER_STATUS('loading'))

		const query = {
			startDate: searchStartDate,
			endDate: searchEndDate,
			status: searchStatus,
			searchKeyword: searchKeyword,
		}
		const apiRes: ResType | undefined = (yield call(apiGetOrder, query)) as ResType

		if (apiRes.status === 200) {
			const newData: any[] = apiRes.data
			const newOrderList: OrderType[] = newData.map(aData => new Order(aData))

			yield put(orderActions.ORDER_LIST(newOrderList))

			if (newOrderList.length > 0) {
				yield put(orderActions.ORDER_STATUS('ok'))
			} else {
				yield put(orderActions.ORDER_STATUS('empty'))
			}
		} else {
			yield put(orderActions.ORDER_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* searchStartDateSaga() {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}
function* searchEndDateSaga() {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}
function* searchStatusSaga() {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}
function* searchKeywordSaga() {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}

function* selectedOrderIdsSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
		const orderList: OrderType[] = yield select(state => state.order.orderList)

		const selectedOrderList: OrderType[] = orderList.filter(anOrder =>
			selectedOrderIds.includes(anOrder.id),
		)
		const selectedStatusArr = selectedOrderList.reduce(
			(statusArr: string[], anOrder: OrderType) => {
				const newStatusArr = [...statusArr]

				if (!newStatusArr.includes(anOrder.status)) {
					newStatusArr.push(anOrder.status)
				}

				return newStatusArr
			},
			[],
		)

		yield put(orderActions.SELECTED_STATUS_ARR(selectedStatusArr))
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerOrderPreparingSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
	} catch (error) {
		Logger.warn(error)
	}
}
function* triggerOrderLabelPrintingSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
	} catch (error) {
		Logger.warn(error)
	}
}
function* triggerUpdateOrderTransitSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
	} catch (error) {
		Logger.warn(error)
	}
}
function* triggerDeleteOrderSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
	} catch (error) {
		Logger.warn(error)
	}
}
function* triggerRefundOrderSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(orderActions.TRIGGER_INIT_ORDER_PAGE.type, triggerInitOrderPage)
	yield takeEvery(orderActions.TRIGGER_ORDER_SEARCH.type, triggerOrderSearchSaga)
	yield takeEvery(orderActions.TRIGGER_ORDER_PREPARING.type, triggerOrderPreparingSaga)
	yield takeEvery(
		orderActions.TRIGGER_ORDER_LABEL_PRINTING.type,
		triggerOrderLabelPrintingSaga,
	)
	yield takeEvery(
		orderActions.TRIGGER_UPDATE_ORDER_TRANSIT.type,
		triggerUpdateOrderTransitSaga,
	)
	yield takeEvery(orderActions.TRIGGER_DELETE_ORDER.type, triggerDeleteOrderSaga)
	yield takeEvery(orderActions.TRIGGER_REFUND_ORDER.type, triggerRefundOrderSaga)

	yield takeEvery(orderActions.SEARCH_START_DATE.type, searchStartDateSaga)
	yield takeEvery(orderActions.SEARCH_END_DATE.type, searchEndDateSaga)
	yield takeEvery(orderActions.SEARCH_STATUS.type, searchStatusSaga)
	yield takeEvery(orderActions.SEARCH_KEYWORD.type, searchKeywordSaga)

	yield takeEvery(orderActions.SET_SELECTED_YEAR.type, fetchOrderOptions)
	yield takeEvery(orderActions.IS_DELETED_INCLUDING.type, fetchOrderOptions)
	yield takeEvery(orderActions.DELETE_ORDER.type, deleteOrderSaga)
	yield takeEvery(orderActions.REGISTER_ORDER.type, registerOrderSaga)
	yield takeEvery(orderActions.UPDATE_ORDER.type, updateOrderSaga)

	yield takeEvery(orderActions.SELECTED_ORDER_IDS.type, selectedOrderIdsSaga)
}

// saga
export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
